import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { getNormalizedActiveLanguage, getPickersLocale } from 'helpers/locale';
import { GlobalStyles } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Moment from 'moment';
import { getActiveLanguage } from '@haldor/ui';
import { FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';

/**
 * StartEndInputs
 * Props:
 * - input: { name, value, onChange } (react-final-form) OR you can pass value/onChange directly
 * - name
 * - value: { start: string, end: string }
 * - onChange: function(updatedValue)
 * - disabled: boolean
 * - labels: { startLabel, endLabel } optional
 * - translate: translation function
 */
const StartEndInputs = (props) => {
	const { input, name, value, onChange, disabled, labels, translate } = props;
	const [includeTime, setIncludeTime] = useState(false);
	const hasDateTime = (v) => typeof v === 'string' && v.includes('T');

	const current = (input && input.value) || value || { start: '', end: '' };

	// Initialize includeTime based on stored values - if any value has T, we're in datetime mode
	useEffect(() => {
		setIncludeTime(hasDateTime(current.start) || hasDateTime(current.end));
	}, [current.start, current.end]);

	const emitChange = (next) => {
		if (input && input.onChange) {
			input.onChange(next);
		}
		if (onChange) onChange(next);
	};

	const setField = (field, v) => {
		const next = { ...(current || { start: '', end: '' }), [field]: v };
		emitChange(next);
	};

	const handleIncludeTimeChange = (event) => {
		const newIncludeTime = event.target.value === 'true';
		setIncludeTime(newIncludeTime);

		// When switching to date+time mode, convert existing dates to datetime format
		if (newIncludeTime && (current.start || current.end) && (!hasDateTime(current.start) || !hasDateTime(current.end))) {
			const startMoment = current.start ? Moment(current.start, 'YYYY-MM-DD') : null;
			const endMoment = current.end ? Moment(current.end, 'YYYY-MM-DD') : null;

			const startWithTime = startMoment && startMoment.isValid() ? startMoment.hours(0).minutes(0).seconds(0).format('YYYY-MM-DDTHH:mm') : '';
			const endWithTime = endMoment && endMoment.isValid() ? endMoment.hours(23).minutes(59).seconds(0).format('YYYY-MM-DDTHH:mm') : '';

			emitChange({ start: startWithTime, end: endWithTime });
		}
		// When switching to date-only mode, convert datetime to date format
		else if (!newIncludeTime && (hasDateTime(current.start) || hasDateTime(current.end))) {
			const startDate = current.start ? Moment(current.start, 'YYYY-MM-DDTHH:mm').format('YYYY-MM-DD') : '';
			const endDate = current.end ? Moment(current.end, 'YYYY-MM-DDTHH:mm').format('YYYY-MM-DD') : '';

			emitChange({ start: startDate, end: endDate });
		}
	};

	const DATE_FORMAT = 'YYYY-MM-DD';
	const DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm';

	// Parse the stored value into a Moment object - intelligently detects format
	const parseMoment = (val) => {
		if (!val) return null;
		// Try DATETIME_FORMAT first if it contains T (ISO format indicator)
		if (val.includes('T')) {
			const m = Moment(val, DATETIME_FORMAT);
			if (m.isValid()) return m;
		}
		// Fall back to DATE_FORMAT
		const m = Moment(val, DATE_FORMAT);
		if (m.isValid()) return m;
		// Last resort: let Moment parse flexibly
		return Moment(val);
	};

	const setDateField = (field, date) => {
		if (!date) {
			setField(field, '');
			return;
		}
		if (includeTime) {
			// Preserve existing time, or default (00:00 for start, 23:59 for end)
			const existingVal = current[field];
			let existingMoment = null;
			if (existingVal) {
				existingMoment = parseMoment(existingVal);
			}
			const hours = existingMoment && existingMoment.isValid() ? existingMoment.hours() : (field === 'end' ? 23 : 0);
			const minutes = existingMoment && existingMoment.isValid() ? existingMoment.minutes() : (field === 'end' ? 59 : 0);
			const val = date.clone().hours(hours).minutes(minutes).seconds(0).format(DATETIME_FORMAT);
			setField(field, val);
		} else {
			setField(field, date.format(DATE_FORMAT));
		}
	};

	const setDateTimeField = (field, dateTime) => {
		if (!dateTime || !dateTime.isValid()) {
			setField(field, '');
			return;
		}
		setField(field, dateTime.clone().seconds(0).format(DATETIME_FORMAT));
	};

	// prefer explicit labels passed in `labels`, otherwise use static defaults
	const finalStartLabel = (labels && labels.startLabel);
	const finalEndLabel = (labels && labels.endLabel);

	// determine locale using Moment (we use Moment for parsing/formatting)
	const active = getActiveLanguage();
	Moment.locale(active);

	// compute inputFormat similar to DatePickers.getDateFormat using Moment
	const getDateFormat = () => {
		let fmt = Moment.localeData(active).longDateFormat('L') || 'YYYY-MM-DD';
		fmt = fmt.replace('YYYY', 'yyyy');
		fmt = fmt.replace('DD', 'dd');
		return fmt;
	};

	const inputFormat = getDateFormat();

	return (
		<>
			<GlobalStyles styles={{ '.MuiPickersPopper-root': { zIndex: 1000000000 + ' !important' } }} />

			{/* Radio buttons for choosing between Date and Date+Time */}
			<Box sx={{ mb: 2 }}>
				<RadioGroup
					row
					value={includeTime.toString()}
					onChange={handleIncludeTimeChange}
				>
					<FormControlLabel
						value="false"
						control={<Radio />}
						label={translate ? translate('Date') || 'Date' : 'Date'}
					/>
					<FormControlLabel
						value="true"
						control={<Radio />}
						label={translate ? translate('Date and time') || 'Date and time' : 'Date and time'}
					/>
				</RadioGroup>
			</Box>

			<LocalizationProvider
				dateAdapter={AdapterMoment}
				adapterLocale={getNormalizedActiveLanguage()}
				localeText={(() => {
					const pl = getPickersLocale(getNormalizedActiveLanguage());
					return pl?.components?.MuiLocalizationProvider?.defaultProps?.localeText || undefined;
				})()}
			>
				<Box display="flex" gap={2} alignItems="center" width="100%">
					<Box flex={1} display="flex" flexDirection="column" gap={1}>
						{includeTime ? (
							<DateTimePicker
								label={finalStartLabel}
								value={parseMoment(current.start)}
								onChange={(dateTime) => setDateTimeField('start', dateTime)}
								disabled={disabled}
								slots={{ textField: TextField }}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						) : (
							<DatePicker
								label={finalStartLabel}
								value={parseMoment(current.start)}
								onChange={(date) => setDateField('start', date)}
								disabled={disabled}
								slots={{ textField: TextField }}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						)}
					</Box>

					<Box flex={1} display="flex" flexDirection="column" gap={1}>
						{includeTime ? (
							<DateTimePicker
								label={finalEndLabel}
								value={parseMoment(current.end)}
								onChange={(dateTime) => setDateTimeField('end', dateTime)}
								disabled={disabled}
								minDateTime={current.start ? parseMoment(current.start) : undefined}
								slots={{ textField: TextField }}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						) : (
							<DatePicker
								label={finalEndLabel}
								value={parseMoment(current.end)}
								onChange={(date) => setDateField('end', date)}
								disabled={disabled}
								minDate={current.start ? parseMoment(current.start) : undefined}
								slots={{ textField: TextField }}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						)}
					</Box>
				</Box>
			</LocalizationProvider></>
	);
};

export default StartEndInputs;
